import { useState, useMemo, useCallback } from 'react';
import {
  Download,
  FileSpreadsheet,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  ArrowUpDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { DataEditor, DisplayValue, FieldTypeIcon } from './DataEditor';
import {
  useUpdateExtraction,
  useDeleteExtraction,
  extractionsToCSV,
  downloadCSV,
} from '@/features/export/hooks/useExport';
import type { ExtractionWithSource, ExtractionField } from '@/features/export/hooks/useExport';

// =========================================
// ExtractionTable — editable data extraction table
// =========================================

interface ExtractionTableProps {
  extractions: ExtractionWithSource[];
  schema: ExtractionField[];
  className?: string;
}

type SortField = 'source' | string;
type SortOrder = 'asc' | 'desc';

interface EditingCell {
  extractionId: string;
  fieldName: string;
}

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  processing: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function ExtractionTable({
  extractions,
  schema,
  className,
}: ExtractionTableProps) {
  const [sortField, setSortField] = useState<SortField>('source');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);

  const updateExtraction = useUpdateExtraction();
  const deleteExtraction = useDeleteExtraction();

  // Sort extractions
  const sortedExtractions = useMemo(() => {
    return [...extractions].sort((a, b) => {
      const mul = sortOrder === 'asc' ? 1 : -1;

      if (sortField === 'source') {
        const nameA = a.sources?.title || a.sources?.file_name || '';
        const nameB = b.sources?.title || b.sources?.file_name || '';
        return mul * nameA.localeCompare(nameB);
      }

      // Sort by field value
      const valA = a.fields[sortField];
      const valB = b.fields[sortField];

      if (valA === null || valA === undefined) return mul;
      if (valB === null || valB === undefined) return -mul;

      if (typeof valA === 'number' && typeof valB === 'number') {
        return mul * (valA - valB);
      }

      return mul * String(valA).localeCompare(String(valB));
    });
  }, [extractions, sortField, sortOrder]);

  const toggleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField]
  );

  const handleCellSave = useCallback(
    (extractionId: string, fieldName: string, value: unknown) => {
      const extraction = extractions.find((e) => e.id === extractionId);
      if (!extraction) return;

      const updatedFields = { ...extraction.fields, [fieldName]: value };
      updateExtraction.mutate({ id: extractionId, fields: updatedFields });
      setEditingCell(null);
    },
    [extractions, updateExtraction]
  );

  const handleExportCSV = useCallback(() => {
    const csv = extractionsToCSV(extractions, schema);
    downloadCSV(csv, `extraction-${Date.now()}.csv`);
  }, [extractions, schema]);

  const handleExportExcel = useCallback(() => {
    // Use xlsx library for Excel export
    import('xlsx').then((XLSX) => {
      const headers = ['Source', ...schema.map((f) => f.name)];
      const rows = extractions.map((ext) => {
        const sourceName =
          ext.sources?.title || ext.sources?.file_name || 'Unknown';
        const values = schema.map((f) => {
          const val = ext.fields[f.name];
          if (val === null || val === undefined) return '';
          if (Array.isArray(val)) return val.join('; ');
          return val;
        });
        return [sourceName, ...values];
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Extractions');
      XLSX.writeFile(wb, `extraction-${Date.now()}.xlsx`);
    });
  }, [extractions, schema]);

  if (extractions.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-12 text-center">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            No extractions yet. Run an extraction to see results here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base">Extraction Table</CardTitle>
            <Badge variant="outline" className="text-xs">
              {extractions.length} sources &middot; {schema.length} fields
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleExportCSV}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  CSV
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export as CSV</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleExportExcel}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                  Excel
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export as Excel</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                {/* Source column */}
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left min-w-[180px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-semibold text-slate-700 hover:text-slate-900"
                    onClick={() => toggleSort('source')}
                  >
                    Source
                    <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </th>
                {/* Status column */}
                <th className="px-3 py-3 text-center min-w-[80px]">
                  <span className="font-semibold text-slate-700 text-xs">
                    Status
                  </span>
                </th>
                {/* Field columns */}
                {schema.map((field) => (
                  <th
                    key={field.name}
                    className="px-4 py-3 text-left min-w-[140px] max-w-[220px]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 font-semibold text-slate-700 hover:text-slate-900"
                      onClick={() => toggleSort(field.name)}
                    >
                      <FieldTypeIcon type={field.type} />
                      <span className="ml-1.5 truncate">{field.name}</span>
                      <ArrowUpDown className="ml-1 h-3 w-3 shrink-0" />
                    </Button>
                  </th>
                ))}
                {/* Actions column */}
                <th className="px-3 py-3 text-center w-[80px]">
                  <span className="font-semibold text-slate-700 text-xs">
                    Actions
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedExtractions.map((extraction) => (
                <ExtractionRow
                  key={extraction.id}
                  extraction={extraction}
                  schema={schema}
                  editingCell={editingCell}
                  onStartEdit={(fieldName) =>
                    setEditingCell({
                      extractionId: extraction.id,
                      fieldName,
                    })
                  }
                  onCellSave={(fieldName, value) =>
                    handleCellSave(extraction.id, fieldName, value)
                  }
                  onCancelEdit={() => setEditingCell(null)}
                  onDelete={() => deleteExtraction.mutate(extraction.id)}
                  isDeleting={deleteExtraction.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// =========================================
// ExtractionRow
// =========================================

interface ExtractionRowProps {
  extraction: ExtractionWithSource;
  schema: ExtractionField[];
  editingCell: EditingCell | null;
  onStartEdit: (fieldName: string) => void;
  onCellSave: (fieldName: string, value: unknown) => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function ExtractionRow({
  extraction,
  schema,
  editingCell,
  onStartEdit,
  onCellSave,
  onCancelEdit,
  onDelete,
  isDeleting,
}: ExtractionRowProps) {
  const sourceName =
    extraction.sources?.title || extraction.sources?.file_name || 'Untitled';
  const isEditable = extraction.status === 'completed';

  return (
    <tr className="border-b hover:bg-slate-50/50 transition-colors">
      {/* Source name */}
      <td className="sticky left-0 z-10 bg-white hover:bg-slate-50/50 px-4 py-2.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-slate-700 font-medium text-xs truncate block max-w-[160px]">
              {sourceName}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <p className="text-xs">{sourceName}</p>
          </TooltipContent>
        </Tooltip>
      </td>

      {/* Status */}
      <td className="px-3 py-2.5 text-center">
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] px-1.5',
            STATUS_STYLES[extraction.status] ?? STATUS_STYLES.pending
          )}
        >
          {extraction.status === 'processing' && (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          )}
          {extraction.status}
        </Badge>
      </td>

      {/* Field cells */}
      {schema.map((field) => {
        const isEditing =
          editingCell?.extractionId === extraction.id &&
          editingCell?.fieldName === field.name;
        const value = extraction.fields[field.name];

        return (
          <td key={field.name} className="px-4 py-2.5">
            {isEditing ? (
              <DataEditor
                field={field}
                value={value}
                onSave={(v) => onCellSave(field.name, v)}
                onCancel={onCancelEdit}
              />
            ) : (
              <button
                type="button"
                className={cn(
                  'w-full text-left rounded px-1 py-0.5 -mx-1',
                  isEditable &&
                    'hover:bg-indigo-50 hover:ring-1 hover:ring-indigo-200 cursor-pointer transition-all'
                )}
                onClick={() => isEditable && onStartEdit(field.name)}
                disabled={!isEditable}
              >
                <DisplayValue field={field} value={value} />
              </button>
            )}
          </td>
        );
      })}

      {/* Actions */}
      <td className="px-3 py-2.5 text-center">
        <div className="flex items-center justify-center gap-1">
          {isEditable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-indigo-600"
                  onClick={() => onStartEdit(schema[0]?.name ?? '')}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit fields</TooltipContent>
            </Tooltip>
          )}
          <AlertDialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-rose-600"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
              </TooltipTrigger>
              <TooltipContent>Delete extraction</TooltipContent>
            </Tooltip>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete extraction?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the extracted data for &ldquo;
                  {sourceName}&rdquo;. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </td>
    </tr>
  );
}
